// components/GroupsTab.tsx
// Composant autonome — toute la logique groupes extraite de MessagesScreen
// Expose reload() via forwardRef pour permettre au parent de déclencher une synchronisation
// Les données sont mises en cache (AsyncStorage) : aucune requête à l'ouverture si le cache existe.
// La synchronisation se fait manuellement via le bouton ou reload().

import React, {
  useState, useEffect, useCallback, useRef,
  forwardRef, useImperativeHandle,
} from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, Platform, ActivityIndicator,
} from 'react-native';
import { Ionicons }   from '@expo/vector-icons';
import AsyncStorage   from '@react-native-async-storage/async-storage';
import * as Haptics   from 'expo-haptics';
import { useTranslation } from 'react-i18next';

// ─── Constantes ───────────────────────────────────────────────────────────────

const WS_BASE      = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const SESSION_KEY  = 'harmonia_session';
const TOKEN_MARGIN = 60;

type GroupKind = 'mine' | 'explore';

const cacheKey    = (kind: GroupKind, userId: string) => `harmonia_groups_${kind}_${userId}`;
const lastSyncKey = (userId: string) => `harmonia_groups_lastsync_${userId}`;

// ─── Types exportés ───────────────────────────────────────────────────────────

export interface GroupItem {
  id:           string;
  name:         string;
  description:  string | null;
  created_at:   string;
  member_count: number;
  is_member?:   boolean;
  last_message: {
    content:     string;
    created_at:  string;
    sender_name: string;
    is_from_me:  boolean;
  } | null;
}

export interface GroupsTabRef {
  reload: () => Promise<void>;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface GroupsTabProps {
  userId:          string;
  searchQuery:     string;
  onOpenGroupChat: (group: GroupItem) => void;
  userRole:        string;
}

// ─── Token ────────────────────────────────────────────────────────────────────

async function getValidToken(): Promise<string | null> {
  try {
    const raw = await AsyncStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session      = JSON.parse(raw);
    const accessToken  = session?.access_token  ?? null;
    const refreshToken = session?.refresh_token  ?? null;
    const expiresAt    = session?.expires_at     ?? 0;
    if (!accessToken || !refreshToken) return null;
    const nowSeconds = Math.floor(Date.now() / 1000);
    if (expiresAt - nowSeconds > TOKEN_MARGIN) return accessToken;
    const res = await fetch(`${WS_BASE}/refresh-token`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return null;
    const { access_token, refresh_token: newRefresh, expires_at } = await res.json();
    await AsyncStorage.setItem(SESSION_KEY, JSON.stringify({
      ...session, access_token, refresh_token: newRefresh ?? refreshToken, expires_at,
    }));
    return access_token;
  } catch { return null; }
}

async function groupsApi(action: string, userId: string, extra?: any): Promise<any> {
  const token = await getValidToken();
  if (!token) return { success: false, error: 'session_expired' };
  const res = await fetch(`${WS_BASE}/groups`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, user_id: userId, access_token: token, ...extra }),
  });
  return res.json();
}

// ─── Utilitaires ──────────────────────────────────────────────────────────────

function formatTime(d: string, t: (key: string, opts?: any) => string, lang: string): string {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const m   = Math.floor((now.getTime() - date.getTime()) / 60000);
  if (m < 1)  return t('groupsTab.time.now');
  if (m < 60) return t('groupsTab.time.minutes', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('groupsTab.time.hours', { count: h });
  const day = Math.floor(h / 24);
  if (day < 7) return t('groupsTab.time.days', { count: day });
  const localeMap: Record<string, string> = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', pt: 'pt-BR' };
  return date.toLocaleDateString(localeMap[lang] || 'fr-FR', { day: '2-digit', month: '2-digit' });
}

function haptic() {
  if (Platform.OS !== 'web') Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
}

function LoadingText({ t }: { t: (key: string) => string }) {
  return (
    <View style={{ alignItems: 'center', paddingVertical: 40 }}>
      <Text style={{ fontSize: 14, color: '#999' }}>{t('groupsTab.loadingData')}</Text>
    </View>
  );
}

function EmptyState({ icon, title, subtitle, children }: {
  icon: string; title: string; subtitle?: string; children?: React.ReactNode;
}) {
  return (
    <View style={{ alignItems: 'center', paddingVertical: 60, paddingHorizontal: 20 }}>
      <Ionicons name={icon as any} size={60} color="#ccc" />
      <Text style={{ fontSize: 17, fontWeight: '700', color: '#888', marginTop: 15, textAlign: 'center' }}>
        {title}
      </Text>
      {subtitle && (
        <Text style={{ fontSize: 14, color: '#aaa', marginTop: 6, textAlign: 'center' }}>
          {subtitle}
        </Text>
      )}
      {children}
    </View>
  );
}

// ─── Composant ────────────────────────────────────────────────────────────────

const GroupsTab = forwardRef<GroupsTabRef, GroupsTabProps>((
  { userId, searchQuery, onOpenGroupChat, userRole },
  ref,
) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'fr';

  const [groupSubTab,  setGroupSubTab]  = useState<GroupKind>('mine');
  const [myGroups,     setMyGroups]     = useState<GroupItem[]>([]);
  const [allGroups,    setAllGroups]    = useState<GroupItem[]>([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupAction,  setGroupAction]  = useState<string | null>(null);
  const [syncing,      setSyncing]      = useState(false);
  const [lastSync,     setLastSync]     = useState<string | null>(null);

  // Indique, par sous-onglet, si les données ont déjà été chargées (cache ou réseau)
  const loadedRef = useRef<Record<GroupKind, boolean>>({ mine: false, explore: false });

  // ── Cache ─────────────────────────────────────────────────────────────────
  const readCache = useCallback(async (kind: GroupKind): Promise<GroupItem[] | null> => {
    try {
      const raw = await AsyncStorage.getItem(cacheKey(kind, userId));
      return raw ? (JSON.parse(raw) as GroupItem[]) : null;
    } catch { return null; }
  }, [userId]);

  const applyList = useCallback((kind: GroupKind, list: GroupItem[]) => {
    kind === 'mine' ? setMyGroups(list) : setAllGroups(list);
  }, []);

  // ── Réseau (+ écriture du cache) ──────────────────────────────────────────
  const fetchAndCache = useCallback(async (kind: GroupKind): Promise<boolean> => {
    if (!userId) return false;
    try {
      const d = await groupsApi(kind === 'mine' ? 'getUserGroups' : 'getAllGroups', userId);
      if (!d.success) return false;
      const list: GroupItem[] = d.groups ?? [];
      applyList(kind, list);
      loadedRef.current[kind] = true;
      await AsyncStorage.setItem(cacheKey(kind, userId), JSON.stringify(list));
      return true;
    } catch { return false; }
  }, [userId, applyList]);

  // Rafraîchit les deux listes et mémorise l'heure de synchro
  const refreshBoth = useCallback(async () => {
    const [a, b] = await Promise.all([fetchAndCache('mine'), fetchAndCache('explore')]);
    if (a || b) {
      const now = new Date().toISOString();
      setLastSync(now);
      try { await AsyncStorage.setItem(lastSyncKey(userId), now); } catch {}
    }
  }, [fetchAndCache, userId]);

  // Synchronisation manuelle (bouton)
  const syncAll = useCallback(async () => {
    if (!userId || syncing) return;
    setSyncing(true);
    try { await refreshBoth(); }
    finally { setSyncing(false); }
  }, [userId, syncing, refreshBoth]);

  // ── Ref exposé au parent ───────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    reload: syncAll,
  }), [syncAll]);

  // ── Changement d'utilisateur : on repart de zéro ──────────────────────────
  useEffect(() => {
    loadedRef.current = { mine: false, explore: false };
    setMyGroups([]);
    setAllGroups([]);
    setLastSync(null);
    if (!userId) return;
    AsyncStorage.getItem(lastSyncKey(userId)).then(v => { if (v) setLastSync(v); }).catch(() => {});
  }, [userId]);

  // ── Chargement : cache d'abord, réseau seulement si aucun cache ───────────
  useEffect(() => {
    if (!userId) return;
    if (loadedRef.current[groupSubTab]) return;

    let cancelled = false;
    (async () => {
      setGroupLoading(true);
      try {
        const cached = await readCache(groupSubTab);
        if (cancelled) return;
        if (cached) {
          applyList(groupSubTab, cached);
          loadedRef.current[groupSubTab] = true;
        } else {
          // Première fois uniquement : une requête, puis tout est en cache
          const ok = await fetchAndCache(groupSubTab);
          if (ok && !cancelled) {
            const now = new Date().toISOString();
            setLastSync(now);
            AsyncStorage.setItem(lastSyncKey(userId), now).catch(() => {});
          }
        }
      } finally {
        if (!cancelled) setGroupLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [userId, groupSubTab, readCache, applyList, fetchAndCache]);

  // ── Actions (rejoindre / quitter) → mettent à jour les listes ET le cache ──
  const joinGroup = async (group: GroupItem) => {
    setGroupAction(group.id);
    try {
      await groupsApi('joinGroup', userId, { group_id: group.id });
      await refreshBoth();
    } catch {}
    finally { setGroupAction(null); }
  };

  const leaveGroup = (group: GroupItem) => {
    if (Platform.OS !== 'web') Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setGroupAction(group.id);
    groupsApi('leaveGroup', userId, { group_id: group.id })
      .then(() => refreshBoth())
      .catch(() => {})
      .finally(() => setGroupAction(null));
  };

  // ── Filtrage recherche ─────────────────────────────────────────────────────
  const q = searchQuery.toLowerCase().trim();
  const filteredMyGroups  = q
    ? myGroups.filter(g => g.name.toLowerCase().includes(q) || (g.description ?? '').toLowerCase().includes(q))
    : myGroups;
  const filteredAllGroups = q
    ? allGroups.filter(g => g.name.toLowerCase().includes(q) || (g.description ?? '').toLowerCase().includes(q))
    : allGroups;

  // ── Rendu ──────────────────────────────────────────────────────────────────
  return (
    <View style={S.section}>

      {/* Sous-onglets Mine / Explorer + bouton de synchronisation */}
      <View style={S.headerRow}>
        <View style={S.subTabs}>
          {(['mine', 'explore'] as const).map(sub => (
            <TouchableOpacity
              key={sub}
              style={[S.subTab, groupSubTab === sub && S.subTabActive]}
              onPress={() => { haptic(); setGroupSubTab(sub); }}
            >
              <Text style={[S.subTabTxt, groupSubTab === sub && S.subTabTxtActive]}>
                {sub === 'mine'
                  ? t('groupsTab.tabs.mine', { count: myGroups.length })
                  : t('groupsTab.tabs.explore')}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        <TouchableOpacity
          style={S.syncBtn}
          onPress={() => { haptic(); syncAll(); }}
          disabled={syncing}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={t('groupsTab.sync', { defaultValue: 'Synchroniser' })}
        >
          {syncing
            ? <ActivityIndicator size="small" color="#8A2BE2" />
            : <Ionicons name="sync-outline" size={22} color="#8A2BE2" />}
        </TouchableOpacity>
      </View>

      {lastSync && (
        <Text style={S.lastSyncTxt}>
          {t('groupsTab.lastSync', {
            time: formatTime(lastSync, t, lang),
            defaultValue: 'Dernière synchro : {{time}}',
          })}
        </Text>
      )}

      {groupLoading ? (
        <LoadingText t={t} />

      ) : groupSubTab === 'mine' ? (
        filteredMyGroups.length === 0 ? (
          <View>
            {q
              ? <EmptyState icon="search-outline" title={t('groupsTab.empty.noResults')} subtitle={t('groupsTab.empty.noResultsFor', { query: searchQuery })} />
              : (
                <EmptyState icon="people-circle-outline" title={t('groupsTab.empty.noGroups')}>
                  <TouchableOpacity onPress={() => setGroupSubTab('explore')} style={S.ctaBtn}>
                    <Text style={S.ctaBtnTxt}>{t('groupsTab.empty.exploreCta')}</Text>
                  </TouchableOpacity>
                </EmptyState>
              )
            }
          </View>
        ) : (
          filteredMyGroups.map(g => (
            <View key={g.id} style={S.groupCard}>
              <TouchableOpacity
                style={{ flex: 1, flexDirection: 'row', alignItems: 'center', padding: 14 }}
                onPress={() => { haptic(); onOpenGroupChat(g); }}
                activeOpacity={0.7}
              >
                <View style={[S.avatar, S.groupAvatar]}>
                  <Ionicons name="people" size={24} color="#fff" />
                </View>
                <View style={S.itemBody}>
                  <View style={S.itemRow}>
                    <Text style={S.itemName} numberOfLines={1}>{g.name}</Text>
                    {g.last_message && (
                      <Text style={S.itemTime}>{formatTime(g.last_message.created_at, t, lang)}</Text>
                    )}
                  </View>
                  <Text style={S.itemLast} numberOfLines={1}>
                    {g.last_message
                      ? t('groupsTab.lastMessage', {
                          sender: g.last_message.is_from_me ? t('groupsTab.you') : g.last_message.sender_name,
                          content: g.last_message.content,
                        })
                      : t('groupsTab.memberCount', { count: g.member_count })}
                  </Text>
                </View>
              </TouchableOpacity>
              <TouchableOpacity
                style={S.leaveBtn}
                onPress={() => leaveGroup(g)}
                disabled={groupAction === g.id}
              >
                <Ionicons
                  name="exit-outline"
                  size={20}
                  color={groupAction === g.id ? '#ccc' : '#DC2626'}
                />
              </TouchableOpacity>
            </View>
          ))
        )

      ) : (
        filteredAllGroups.length === 0 ? (
          q
            ? <EmptyState icon="search-outline" title={t('groupsTab.empty.noResults')} subtitle={t('groupsTab.empty.noResultsFor', { query: searchQuery })} />
            : <EmptyState icon="search-outline" title={t('groupsTab.empty.noGroupsAvailable')} />
        ) : (
          filteredAllGroups.map(g => (
            <View key={g.id} style={S.exploreCard}>
              <View style={[S.avatar, S.groupAvatar]}>
                <Ionicons name="people" size={24} color="#fff" />
              </View>
              <View style={[S.itemBody, { marginLeft: 12 }]}>
                <Text style={S.itemName}>{g.name}</Text>
                <Text style={{ fontSize: 13, color: '#666' }} numberOfLines={1}>
                  {g.description || t('groupsTab.memberCount', { count: g.member_count })}
                </Text>
              </View>
              {g.is_member ? (
                <TouchableOpacity style={S.openBtn} onPress={() => { haptic(); onOpenGroupChat(g); }}>
                  <Ionicons name="chatbubbles-outline" size={15} color="#8A2BE2" />
                  <Text style={S.openBtnTxt}>{t('groupsTab.open')}</Text>
                </TouchableOpacity>
              ) : (
                <TouchableOpacity
                  style={S.joinBtn}
                  onPress={() => joinGroup(g)}
                  disabled={groupAction === g.id}
                >
                  <Text style={S.joinBtnTxt}>{groupAction === g.id ? t('groupsTab.joining') : t('groupsTab.join')}</Text>
                </TouchableOpacity>
              )}
            </View>
          ))
        )
      )}
    </View>
  );
});

GroupsTab.displayName = 'GroupsTab';
export default GroupsTab;

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = StyleSheet.create({
  section:        { paddingVertical: 8 },
  headerRow:      { flexDirection: 'row', alignItems: 'center', marginHorizontal: 10, marginBottom: 6 },
  subTabs:        { flex: 1, flexDirection: 'row', backgroundColor: '#F0E8FF', borderRadius: 12, padding: 4 },
  subTab:         { flex: 1, paddingVertical: 9, alignItems: 'center', borderRadius: 10 },
  subTabActive:   { backgroundColor: '#8A2BE2' },
  subTabTxt:      { fontSize: 13, color: '#8A2BE2', fontWeight: '600' },
  subTabTxtActive:{ color: '#fff' },
  syncBtn:        { width: 44, height: 44, marginLeft: 8, borderRadius: 22, backgroundColor: '#F0E8FF', justifyContent: 'center', alignItems: 'center' },
  lastSyncTxt:    { marginHorizontal: 14, marginBottom: 6, fontSize: 11, color: '#aaa', textAlign: 'right' },
  groupCard:      { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', marginHorizontal: 10, marginVertical: 5, borderRadius: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 4, elevation: 2 },
  leaveBtn:       { padding: 16, borderLeftWidth: 1, borderLeftColor: '#F0F0F0', justifyContent: 'center', alignItems: 'center' },
  exploreCard:    { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', padding: 14, marginHorizontal: 10, marginVertical: 5, borderRadius: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 3, elevation: 1 },
  joinBtn:        { backgroundColor: '#8A2BE2', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 8, minWidth: 90, alignItems: 'center' },
  joinBtnTxt:     { color: '#fff', fontSize: 13, fontWeight: '700' },
  openBtn:        { flexDirection: 'row', alignItems: 'center', gap: 4, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8, borderWidth: 1, borderColor: '#8A2BE2' },
  openBtnTxt:     { color: '#8A2BE2', fontSize: 13, fontWeight: '600' },
  ctaBtn:         { alignSelf: 'center', marginTop: 12, paddingHorizontal: 20, paddingVertical: 10, backgroundColor: '#F0E8FF', borderRadius: 10 },
  ctaBtnTxt:      { color: '#8A2BE2', fontWeight: '700', fontSize: 14 },
  avatar:         { width: 50, height: 50, borderRadius: 25, backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center' },
  groupAvatar:    { backgroundColor: '#FF8C00' },
  itemBody:       { flex: 1, marginLeft: 12 },
  itemRow:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  itemName:       { fontSize: 15, fontWeight: '600', color: '#333', flex: 1 },
  itemTime:       { fontSize: 12, color: '#999', marginLeft: 8, flexShrink: 0 },
  itemLast:       { fontSize: 14, color: '#666', flex: 1 },
});
